export const SECTION_LAYOUT_ID_TAG = 'tallCabinetLayoutSectionId';
export const SECTION_HIGHLIGHT_BOX_TAG = 'tallCabinetSectionHighlightBox';
export const SECTION_HIGHLIGHT_COLOR = 0x5ecfff;
export const CABINET_HIGHLIGHT_COLOR = 0x5ecfff;

const SECTION_HIGHLIGHT_BOX_POOL_TAG = 'tallCabinetSectionHighlightBoxes';
const SECTION_HIGHLIGHT_HORIZONTAL_PADDING = 0.015;
const SECTION_HIGHLIGHT_VERTICAL_PADDING = 0.004;

export function isCabinetSectionHighlightBox(object) {
  return Boolean(object && object.userData && object.userData[SECTION_HIGHLIGHT_BOX_TAG]);
}

export function withSectionHighlightBoxesHidden(instance, callback) {
  const hiddenBoxes = [];

  instance.traverse((child) => {
    if (isCabinetSectionHighlightBox(child) && child.visible) {
      hiddenBoxes.push(child);
      child.visible = false;
    }
  });

  try {
    return callback();
  } finally {
    hiddenBoxes.forEach((mesh) => {
      mesh.visible = true;
    });
  }
}

function getSectionOverlayParent(groups, instance) {
  const originalGroups = Object.values(groups)
    .flat()
    .filter((group) => !group.isClone);
  const referenceGroups = originalGroups.length ? originalGroups : Object.values(groups).flat();
  const referenceMesh = referenceGroups
    .flatMap((group) => group.meshes)
    .find((mesh) => mesh && mesh.parent);

  return referenceMesh ? referenceMesh.parent : instance;
}

function getBoxCorners(box) {
  const THREE = window.THREE;

  return [
    new THREE.Vector3(box.min.x, box.min.y, box.min.z),
    new THREE.Vector3(box.min.x, box.min.y, box.max.z),
    new THREE.Vector3(box.min.x, box.max.y, box.min.z),
    new THREE.Vector3(box.min.x, box.max.y, box.max.z),
    new THREE.Vector3(box.max.x, box.min.y, box.min.z),
    new THREE.Vector3(box.max.x, box.min.y, box.max.z),
    new THREE.Vector3(box.max.x, box.max.y, box.min.z),
    new THREE.Vector3(box.max.x, box.max.y, box.max.z),
  ];
}

function getHorizontalBoundsInParentSpace(instance, parent) {
  const bounds = {
    minX: Infinity,
    maxX: -Infinity,
    minZ: Infinity,
    maxZ: -Infinity,
  };

  instance.updateMatrixWorld(true);
  parent.updateMatrixWorld(true);

  instance.traverse((child) => {
    if (!child.isMesh || child.userData[SECTION_HIGHLIGHT_BOX_TAG] || !child.geometry) {
      return;
    }

    if (!child.geometry.boundingBox) {
      child.geometry.computeBoundingBox();
    }

    getBoxCorners(child.geometry.boundingBox).forEach((corner) => {
      const parentPoint = parent.worldToLocal(child.localToWorld(corner.clone()));

      bounds.minX = Math.min(bounds.minX, parentPoint.x);
      bounds.maxX = Math.max(bounds.maxX, parentPoint.x);
      bounds.minZ = Math.min(bounds.minZ, parentPoint.z);
      bounds.maxZ = Math.max(bounds.maxZ, parentPoint.z);
    });
  });

  if (!isFinite(bounds.minX) || !isFinite(bounds.maxX) || !isFinite(bounds.minZ) || !isFinite(bounds.maxZ)) {
    return null;
  }

  return bounds;
}

function createSectionHighlightBox(parent) {
  const THREE = window.THREE;
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshBasicMaterial({
      color: SECTION_HIGHLIGHT_COLOR,
      depthWrite: false,
      opacity: 0,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
      transparent: true,
      side: THREE.DoubleSide,
    })
  );

  mesh.name = 'TallCabinetSectionHighlightBox';
  mesh.renderOrder = 20;
  mesh.userData[SECTION_HIGHLIGHT_BOX_TAG] = true;
  parent.add(mesh);

  return mesh;
}

function resetSectionHighlightBoxGeometry(mesh) {
  const THREE = window.THREE;

  if (mesh.geometry) {
    mesh.geometry.dispose();
  }

  mesh.geometry = new THREE.BoxGeometry(1, 1, 1);
}

export function applySectionHighlightBoxes(instance, groups, sections, layoutStartBottomLocalY, heightBasis, layoutZoneHeight) {
  const parent = getSectionOverlayParent(groups, instance);
  const horizontalBounds = getHorizontalBoundsInParentSpace(instance, parent);

  if (!horizontalBounds) {
    return;
  }

  if (!instance.userData[SECTION_HIGHLIGHT_BOX_POOL_TAG]) {
    instance.userData[SECTION_HIGHLIGHT_BOX_POOL_TAG] = [];
  }

  const pool = instance.userData[SECTION_HIGHLIGHT_BOX_POOL_TAG];
  while (pool.length < sections.length) {
    pool.push(createSectionHighlightBox(parent));
  }

  pool.forEach((mesh, index) => {
    mesh.visible = index < sections.length;
    mesh.material.opacity = 0;

    if (index >= sections.length) {
      delete mesh.userData[SECTION_LAYOUT_ID_TAG];
    }
  });

  const width = horizontalBounds.maxX - horizontalBounds.minX;
  const depth = horizontalBounds.maxZ - horizontalBounds.minZ;
  const centerX = (horizontalBounds.minX + horizontalBounds.maxX) / 2;
  const centerZ = (horizontalBounds.minZ + horizontalBounds.maxZ) / 2;
  let currentBottomLocalY = layoutStartBottomLocalY;

  [...sections].reverse().forEach((section) => {
    const slotHeightLocal = (section.height / heightBasis) * layoutZoneHeight;
    const mesh = pool[sections.indexOf(section)];
    const highlightHeight = Math.max(0.001, slotHeightLocal + SECTION_HIGHLIGHT_VERTICAL_PADDING);

    resetSectionHighlightBoxGeometry(mesh);
    mesh.userData[SECTION_LAYOUT_ID_TAG] = section.id;
    mesh.position.set(centerX, currentBottomLocalY + slotHeightLocal / 2, centerZ);
    mesh.scale.set(
      width + SECTION_HIGHLIGHT_HORIZONTAL_PADDING,
      highlightHeight,
      depth + SECTION_HIGHLIGHT_HORIZONTAL_PADDING
    );
    mesh.visible = true;
    mesh.material.opacity = 0;

    currentBottomLocalY += slotHeightLocal;
  });
}
